/**
 * Projection cartographique du back-office.
 *
 * La carte de supervision est rendue en SVG à partir d'une projection Web Mercator calculée
 * ici, et non par une bibliothèque de cartographie. Deux raisons, l'une technique, l'autre
 * budgétaire :
 *  - le besoin est un nuage de marqueurs d'état sur fond neutre, sans style vectoriel, sans
 *    rotation, sans 3D : MapLibre + son runtime serait une dépendance lourde pour cela ;
 *  - la revue critique (docs/06) pointe que le coût des tuiles n'est ni borné ni chiffré. Un
 *    back-office qui rafraîchit sa carte toutes les minutes est un consommateur de quota
 *    invisible. Ici, le fond de tuiles est OPTIONNEL (VITE_MAP_TILES_URL) : sans lui, la carte
 *    reste exploitable — les marqueurs, leurs positions relatives et leur état suffisent au
 *    diagnostic — et ne coûte rien.
 *
 * Convention : les « coordonnées monde » sont normalisées dans [0,1] × [0,1], origine en haut
 * à gauche (nord-ouest), comme la grille de tuiles standard.
 */

export interface PointMonde {
  x: number;
  y: number;
}

export interface PointLatLon {
  lat: number;
  lon: number;
}

export interface Emprise {
  nord: number;
  sud: number;
  est: number;
  ouest: number;
}

/** Latitude maximale représentable en Web Mercator. */
const LAT_MAX = 85.05112878;

/** Projette une position WGS84 vers les coordonnées monde normalisées. */
export function projeter({ lat, lon }: PointLatLon): PointMonde {
  const latBornee = Math.min(LAT_MAX, Math.max(-LAT_MAX, lat));
  const phi = (latBornee * Math.PI) / 180;
  return {
    x: (lon + 180) / 360,
    y: (1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2,
  };
}

/** Inverse de `projeter`. */
export function deprojeter({ x, y }: PointMonde): PointLatLon {
  const n = Math.PI * (1 - 2 * y);
  return {
    lon: x * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
  };
}

/** Emprise englobant une liste de positions, `null` si la liste est vide. */
export function empriseDe(points: readonly PointLatLon[]): Emprise | null {
  if (points.length === 0) return null;
  let nord = -90;
  let sud = 90;
  let est = -180;
  let ouest = 180;
  for (const p of points) {
    if (p.lat > nord) nord = p.lat;
    if (p.lat < sud) sud = p.lat;
    if (p.lon > est) est = p.lon;
    if (p.lon < ouest) ouest = p.lon;
  }
  return { nord, sud, est, ouest };
}

export interface Vue {
  /** Centre de la vue, en coordonnées monde. */
  centre: PointMonde;
  /** Échelle : nombre de pixels pour la largeur totale du monde. */
  echelle: number;
}

/** Échelle minimale : le monde entier tient dans 256 px (niveau de zoom 0). */
export const ECHELLE_MIN = 256;
/** Échelle maximale : au-delà, les tuiles n'existent plus (niveau de zoom 19). */
export const ECHELLE_MAX = 256 * 2 ** 19;

/**
 * Calcule la vue qui cadre une emprise dans un rectangle donné, avec une marge.
 * Un parc réduit à un seul appareil ne doit pas produire un zoom infini : l'échelle est bornée.
 */
export function cadrer(
  emprise: Emprise,
  largeur: number,
  hauteur: number,
  marge = 48,
): Vue {
  const nordOuest = projeter({ lat: emprise.nord, lon: emprise.ouest });
  const sudEst = projeter({ lat: emprise.sud, lon: emprise.est });

  const centre: PointMonde = {
    x: (nordOuest.x + sudEst.x) / 2,
    y: (nordOuest.y + sudEst.y) / 2,
  };

  const largeurUtile = Math.max(1, largeur - 2 * marge);
  const hauteurUtile = Math.max(1, hauteur - 2 * marge);
  const etendueX = Math.max(sudEst.x - nordOuest.x, 1e-7);
  const etendueY = Math.max(sudEst.y - nordOuest.y, 1e-7);

  const echelle = Math.min(largeurUtile / etendueX, hauteurUtile / etendueY);

  return {
    centre,
    // Un point isolé donnerait une échelle démesurée : on plafonne au zoom 17 (~ le champ).
    echelle: Math.min(Math.max(echelle, ECHELLE_MIN), 256 * 2 ** 17),
  };
}

/** Convertit une position WGS84 en pixels dans le repère du conteneur. */
export function versEcran(
  position: PointLatLon,
  vue: Vue,
  largeur: number,
  hauteur: number,
): PointMonde {
  const m = projeter(position);
  return {
    x: (m.x - vue.centre.x) * vue.echelle + largeur / 2,
    y: (m.y - vue.centre.y) * vue.echelle + hauteur / 2,
  };
}

/** Niveau de zoom entier correspondant à une échelle. */
export function niveauZoom(echelle: number): number {
  return Math.round(Math.log2(echelle / 256));
}

export interface Tuile {
  z: number;
  x: number;
  y: number;
  /** Position et taille en pixels dans le repère du conteneur. */
  gauche: number;
  haut: number;
  taille: number;
}

/**
 * Liste les tuiles couvrant le conteneur pour la vue courante. Renvoie un tableau vide si
 * aucun fond n'est configuré — l'appelant se contente alors du graticule.
 */
export function tuilesVisibles(vue: Vue, largeur: number, hauteur: number): Tuile[] {
  const z = Math.min(19, Math.max(0, niveauZoom(vue.echelle)));
  const nbTuiles = 2 ** z;
  // Taille réelle d'une tuile à l'échelle courante (elle n'est pas 256 px si l'échelle
  // n'est pas exactement une puissance de deux).
  const taille = vue.echelle / nbTuiles;

  const xMin = Math.floor((vue.centre.x - largeur / 2 / vue.echelle) * nbTuiles);
  const xMax = Math.floor((vue.centre.x + largeur / 2 / vue.echelle) * nbTuiles);
  const yMin = Math.floor((vue.centre.y - hauteur / 2 / vue.echelle) * nbTuiles);
  const yMax = Math.floor((vue.centre.y + hauteur / 2 / vue.echelle) * nbTuiles);

  const tuiles: Tuile[] = [];
  for (let y = Math.max(0, yMin); y <= Math.min(nbTuiles - 1, yMax); y += 1) {
    for (let x = xMin; x <= xMax; x += 1) {
      // Enroulement en longitude : la carte se répète d'est en ouest.
      const xEnroule = ((x % nbTuiles) + nbTuiles) % nbTuiles;
      tuiles.push({
        z,
        x: xEnroule,
        y,
        gauche: (x / nbTuiles - vue.centre.x) * vue.echelle + largeur / 2,
        haut: (y / nbTuiles - vue.centre.y) * vue.echelle + hauteur / 2,
        taille,
      });
    }
  }
  return tuiles;
}

/** Construit l'URL d'une tuile à partir du gabarit `{z}/{x}/{y}`. */
export function urlTuile(gabarit: string, tuile: Tuile): string {
  return gabarit
    .replace('{z}', String(tuile.z))
    .replace('{x}', String(tuile.x))
    .replace('{y}', String(tuile.y));
}

/** Distance orthodromique en mètres entre deux positions (formule de haversine). */
export function distanceM(a: PointLatLon, b: PointLatLon): number {
  const R = 6_371_000;
  const rad = (d: number): number => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
